import { Array, identity, Option, pipe, Result, String } from 'effect'

import type {
  ViewConfigFilterNodeForJoinOn,
  ViewConfigPropertyFilter,
} from '../../../../../schemas/view_config/index.ts'
import type { CompileContext } from '../../../common.ts'
import { generateFilterSql } from '../filter/generateFilterSql.ts'

/** ブロックフィルタ用に tableSql を生成 */
export const generateTableSqlForBlockFilter = ({
  additionalJoinOn,
  modelName,
  context,
}: {
  additionalJoinOn: ViewConfigFilterNodeForJoinOn
  modelName: string
  context: CompileContext
}): string | undefined => {
  // 同名モデルの条件句の整合性が取れなくなるため、エイリアスを生成
  const baseModelNameAlias = `base_${modelName}`
  // ブロックフィルタの条件となる outer join と where句を生成する
  const query = generateBlockFilterQuery({
    additionalJoinOn,
    context,
    baseModel: { modelName, aliasName: baseModelNameAlias },
    tableAliasSuffix: 'alias',
  })
  if (query === undefined) return
  // "WITH ~ AS ~"は後で付与されるため、ここではただの select 文を生成する
  return `SELECT "${baseModelNameAlias}".* FROM "${modelName}" AS "${baseModelNameAlias}" ${query.joinQuery ?? ''} WHERE ${query.filterQuery}`
}

interface GenerateBlockFilterQueryResult {
  joinQuery: string | undefined
  filterQuery: string
}

/**
 * additionalJoinOn からブロックフィルタの用のクエリを生成
 * @param additionalJoinOn - ブロックフィルタの条件となるフィルタ条件のツリー構造
 * @param context - シートに関連するモデルの管理やログ出力のためのコンテキスト
 * @param baseModel - join のベースとなるモデル名とエイリアス名
 * @param tableAliasSuffix - 同名モデルの join がエラーにならない様に、テーブル名のエイリアスに suffix を付与
 */
const generateBlockFilterQuery = ({
  additionalJoinOn,
  context,
  baseModel,
  tableAliasSuffix,
}: {
  additionalJoinOn: ViewConfigFilterNodeForJoinOn
  context: CompileContext
  /** join のベースとなるモデル名とエイリアス名 */
  baseModel: { modelName: string; aliasName: string }
  tableAliasSuffix: string
}): GenerateBlockFilterQueryResult | undefined => {
  const { logicalOperator, leafs, children } = additionalJoinOn
  // 子ノードに対して、再帰的に join を生成する
  const childrenQueries = pipe(
    children,
    Array.map((child, index) =>
      generateBlockFilterQuery({
        additionalJoinOn: child,
        context,
        baseModel,
        tableAliasSuffix: `${tableAliasSuffix}_c_${index}`,
      }),
    ),
    Array.flatMapNullishOr(identity),
  )
  // ブロックフィルタの条件となる join と filter のリストを生成する
  const parentQueries = pipe(
    leafs,
    Array.map((leaf, index) =>
      generateBlockFilterSqlWrapper({
        leaf,
        context,
        baseModel,
        tableAliasSuffix: `${tableAliasSuffix}_l_${index}`,
      }),
    ),
    Array.flatMapNullishOr(identity),
  )
  if (childrenQueries.length === 0 && parentQueries.length === 0) return
  // join句と filter句をそれぞれ連結する
  return {
    joinQuery: mergeJoinQuery({ parentQueries, childrenQueries }),
    filterQuery: mergeFilterQuery({ parentQueries, childrenQueries, logicalOperator }),
  }
}

/** join 文を連結する */
const mergeJoinQuery = ({
  parentQueries,
  childrenQueries,
}: {
  parentQueries: GenerateBlockFilterSqlResult[]
  childrenQueries: GenerateBlockFilterQueryResult[]
}): string | undefined => {
  const joins = pipe(
    parentQueries,
    Array.flatMapNullishOr((x) => x.joinSql),
    Array.appendAll(Array.flatMapNullishOr(childrenQueries, (x) => x.joinQuery)),
  )
  if (joins.length === 0) return undefined
  return Array.join(joins, ' ')
}

/**
 * filter 条件を連結する
 * - filter は children 毎に括弧でくくって、logicalOperator で連結する
 */
const mergeFilterQuery = ({
  parentQueries,
  childrenQueries,
  logicalOperator,
}: {
  parentQueries: GenerateBlockFilterSqlResult[]
  childrenQueries: GenerateBlockFilterQueryResult[]
  logicalOperator: 'and' | 'or'
}): string => {
  const filter = pipe(
    parentQueries,
    Array.map((x) => x.filterSql),
    Array.appendAll(Array.map(childrenQueries, (x) => x.filterQuery)),
    Array.join(` ${String.toUpperCase(logicalOperator)} `),
  )
  return `(${filter})`
}

interface GenerateBlockFilterSqlResult {
  /** 参照カラムの場合は、参照先のモデルに対する join 文を生成 */
  joinSql: string | undefined
  filterSql: string
}

/** generateBlockFilterSql の実行エラーをログに出力 */
const generateBlockFilterSqlWrapper = (
  parameters: GenerateBlockFilterSqlParameters,
): GenerateBlockFilterSqlResult | undefined => {
  const {
    context,
    leaf: { property },
  } = parameters
  const result = generateBlockFilterSql(parameters)
  // 想定外の分岐が発生する場合はログに出力
  if (Result.isFailure(result)) {
    context.logs.error(`${result.failure} ${property.nodeName}.${property.propertyName}`)
    return
  }
  return result.success
}

interface GenerateBlockFilterSqlParameters {
  leaf: ViewConfigPropertyFilter
  context: CompileContext
  /** join のベースとなるモデル名とエイリアス名 */
  baseModel: { modelName: string; aliasName: string }
  /** 同名モデルの join がエラーにならない様に、テーブル名のエイリアスに suffix を付与 */
  tableAliasSuffix: string
}

/**
 * leaf からブロックフィルタ用の filter 文を生成
 * @param leaf - ブロックフィルタの条件となるフィルタ条件のうち、プロパティに対するフィルタ条件
 * @param context - シートに関連するモデルの管理やログ出力のためのコンテキスト
 * @param baseModel - join のベースとなるモデル名とエイリアス名
 * @param tableAliasSuffix - 同名モデルの join がエラーにならない様に、テーブル名のエイリアスに suffix を付与
 */
const generateBlockFilterSql = ({
  leaf,
  context,
  baseModel,
  tableAliasSuffix,
}: GenerateBlockFilterSqlParameters): Result.Result<GenerateBlockFilterSqlResult, string> => {
  // leaf.property.modelName とベースとする modelName が同じなら、エイリアス名を使う
  const baseModelName = leaf.property.modelName === baseModel.modelName ? baseModel.aliasName : leaf.property.modelName
  if (leaf.option?.shouldFilterToReferencedEntityName === true) {
    // 参照カラムの場合の条件文の生成
    return generateReferenceJoinAndFilterSql({ leaf, context, baseModelName, tableAliasSuffix })
  }
  const property = context.modelSearcher.searchProperty(leaf.property.modelName, leaf.property.propertyName)
  if (property === undefined) {
    return Result.fail('ブロックフィルタ：絞り込み用のプロパティが見つかりません。')
  }
  const filterCondition = {
    fieldSql: `"${baseModelName}"."${leaf.property.propertyName}"`,
    fieldType: property.type,
    filterType: leaf.filterType,
    filterValue: leaf.filterValue,
    context,
    dateTimeType: leaf.filterDateTimeType,
    shouldConsiderCase: leaf.option?.shouldConsiderCase,
  }
  const filterSql = generateFilterSql(filterCondition)
  if (filterSql === undefined) {
    return Result.fail(`ブロックフィルタ：絞り込みSQLの生成に失敗しました。${JSON.stringify(filterCondition)}`)
  }
  return Result.succeed({ joinSql: undefined, filterSql })
}

const generateReferenceJoinAndFilterSql = ({
  leaf,
  context,
  baseModelName,
  tableAliasSuffix,
}: {
  leaf: ViewConfigPropertyFilter
  context: CompileContext
  /** join のベースとなるモデル名（エイリアス名を生成している場合はエイリアス名を使用）*/
  baseModelName: string
  /** 同名モデルの join がエラーにならない様に、テーブル名のエイリアスに suffix を付与 */
  tableAliasSuffix: string
}): Result.Result<GenerateBlockFilterSqlResult, string> => {
  const { property } = leaf
  // オブジェクトの"referenceTo"を取得する
  const model = context.modelSearcher.searchProperty(property.modelName, property.propertyName)
  if (model?.referenceTo === undefined || model.referenceTo.length === 0) {
    return Result.fail('ブロックフィルタ：参照カラムの参照先が存在しません。')
  }
  const referenceModelNames = Array.dedupeWith(model.referenceTo, (a, b) => a.modelName === b.modelName)
  if (referenceModelNames.length !== 1) {
    // 参照先オブジェクト名が特定の１つに絞り込めないならエラーとする
    // - データ構造的にはありえるが、ブロックフィルタの仕様的には想定されない
    // - 複数の参照オブジェクトが存在する場合、想定と違うオブジェクトに対してのクエリが行われる可能性がある
    return Result.fail(
      `ブロックフィルタ：参照カラムの参照先が複数あるため、特定に失敗しました。 参照先モデル:${referenceModelNames.map((x) => x.modelName).join(', ')}`,
    )
  }
  // 参照カラムの参照先となるオブジェクト名の取得
  // referenceModelNames.length === 1 が保証されているため、Array.headは必ず値を返す
  const referenceTo = pipe(referenceModelNames, Array.head, Option.getOrThrow)
  const referenceModel = context.modelSearcher.searchModel(referenceTo.modelName)
  if (referenceModel === undefined) {
    return Result.fail(
      `ブロックフィルタ：参照カラムの参照先のモデルが存在しません。 参照先モデル:${referenceTo.modelName}`,
    )
  }
  // 参照先オブジェクトの"meta"が"name"のプロパティを取得して、join 文を生成する
  const nameProperty = referenceModel.properties.find((x) => x.meta === 'name')
  if (nameProperty?.name === undefined) {
    return Result.fail(`ブロックフィルタ：「${referenceTo.modelName}」の"name"プロパティが存在しません。`)
  }
  // 同名モデルの条件句の整合性が取れなくなるため、エイリアスを生成
  const referenceModelNameAlias = `${referenceModel.name}_${tableAliasSuffix}`
  // 参照カラムに対してのフィルターSQLを生成
  const filterCondition = {
    fieldSql: `"${referenceModelNameAlias}"."${nameProperty.name}"`,
    fieldType: nameProperty.type,
    filterType: leaf.filterType,
    filterValue: leaf.filterValue,
    context,
    dateTimeType: leaf.filterDateTimeType,
    shouldConsiderCase: leaf.option?.shouldConsiderCase,
  }
  const filterSql = generateFilterSql(filterCondition)
  if (filterSql === undefined) {
    return Result.fail(
      `ブロックフィルタ：参照カラムのフィルターSQLの生成に失敗しました。${JSON.stringify(filterCondition)}`,
    )
  }
  return Result.succeed({
    joinSql: `LEFT JOIN "${referenceModel.name}" AS "${referenceModelNameAlias}" ON "${baseModelName}"."${property.propertyName}" = "${referenceModelNameAlias}"."id"`,
    filterSql,
  })
}
